/* global Word */
import { WordApiError } from '../core/errors';
import { logger } from '../core/logging';
import { cmToPt } from '../core/utils/math';
import { TableBuilder } from '../word/builders';
import { wrapInFlatOpc } from '../word/ooxml/flatOpc';
import type { TemplateProfile } from '../templates';
import type { ElementDefinition } from './types';

const FOOTNOTE_PLACEHOLDER = 'Enter foot note text here';
const FOOTNOTE_SHAPE_NAME = 'FootnoteBox';
const EMU_PER_POINT = 12700;
const DEFAULT_FOOTNOTE_HEIGHT_PT = 36;
const DEFAULT_FOOTNOTE_WIDTH_PT = cmToPt(18.38);
const LINE_HEIGHT_PT = 12;

// ─── XML / color helpers (unchanged) ─────────────────────────────────────────

function escapeXml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

function normalizeHexColor(color: string): string {
  return color.startsWith('#') ? color.slice(1) : color;
}

function toFontSize(profile: TemplateProfile): number {
  const STATIC_FONT_SIZE = 7; // Type your desired static font size here
  
  // Multiplies by 100 to maintain your existing unit scale (e.g., returns 700)
  return STATIC_FONT_SIZE * 100; 
}

function toHex(color: string): string {
  return color.startsWith('#') ? color : `#${color}`;
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── OOXML generator (kept for reference / export, not used for insertion) ────

function generateSecureRandomId(): number {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const arr = new Uint32Array(1);
    crypto.getRandomValues(arr);
    return (arr[0] % 999999999) + 1;
  }
  return Math.floor(Math.random() * 999999999) + 1;
}

export function createFootnoteBodyOoxml(
  profile: TemplateProfile,
  pageWidth: number,
  leftMargin: number,
  rightMargin: number,
  pageHeight: number,
  bottomMargin: number,
  lineCount: number = 1,
  shapeId: number = generateSecureRandomId()
): string {
  const availableWidth     = Math.max(0, pageWidth - leftMargin - rightMargin);
  const shapeHeight        = DEFAULT_FOOTNOTE_HEIGHT_PT + Math.max(0, lineCount - 1) * LINE_HEIGHT_PT;
  const widthEmu           = Math.round(availableWidth * EMU_PER_POINT);
  const heightEmu          = Math.round(shapeHeight * EMU_PER_POINT);
  const leftMarginEmu      = Math.round(leftMargin * EMU_PER_POINT);
  const positionFromTopEmu = Math.max(
    0,
    Math.round(pageHeight * EMU_PER_POINT) -
    Math.round(bottomMargin * EMU_PER_POINT) -
    heightEmu
  );

  const fontSize  = toFontSize(profile);
  const textColor = normalizeHexColor(profile.colorPalette.sourceText || '000000');
  const fontFamily = escapeXml(profile.typography.fontFamily || 'Calibri');
  const paragraphs = Array.from({ length: Math.max(1, lineCount) }, (_, index) => {
    const text = `${index + 1}. ${FOOTNOTE_PLACEHOLDER}`;
    return [
      '<a:p>',
      '<a:pPr>',
      '<a:spcBef><a:spcPts val="0"/></a:spcBef>',
      '<a:spcAft><a:spcPts val="0"/></a:spcAft>',
      '</a:pPr>',
      '<a:r>',
      `<a:rPr lang="en-US" dirty="0" sz="${fontSize}">`,
      `<a:latin typeface="${fontFamily}"/>`,
      '<a:solidFill>',
      `<a:srgbClr val="${textColor}"/>`,
      '</a:solidFill>',
      '</a:rPr>',
      `<a:t>${escapeXml(text)}</a:t>`,
      '</a:r>',
      '</a:p>',
    ].join('');
  }).join('');

  const body = [
    '<w:p>',
    '<w:r>',
    '<w:drawing>',
    `<wp:anchor distT="0" distB="0" distL="114300" distR="114300" simplePos="0" relativeHeight="251658240" behindDoc="0" locked="0" layoutInCell="1" allowOverlap="1">`,
    '<wp:simplePos x="0" y="0"/>',
    '<wp:positionH relativeFrom="page">',
    `<wp:posOffset>${leftMarginEmu}</wp:posOffset>`,
    '</wp:positionH>',
    '<wp:positionV relativeFrom="page">',
    `<wp:posOffset>${positionFromTopEmu}</wp:posOffset>`,
    '</wp:positionV>',
    `<wp:extent cx="${widthEmu}" cy="${heightEmu}"/>`,
    '<wp:effectExtent l="0" t="0" r="0" b="0"/>',
    '<wp:wrapSquare wrapText="bothSides"/>',
    `<wp:docPr id="${shapeId}" name="${escapeAttribute(FOOTNOTE_SHAPE_NAME)}"/>`,
    '<wp:cNvGraphicFramePr>',
    '<a:graphicFrameLocks xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main" noChangeAspect="1"/>',
    '</wp:cNvGraphicFramePr>',
    '<a:graphic xmlns:a="http://schemas.openxmlformats.org/drawingml/2006/main">',
    '<a:graphicData uri="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">',
    '<wps:wsp xmlns:wps="http://schemas.microsoft.com/office/word/2010/wordprocessingShape">',
    '<wps:cNvSpPr/>',
    '<wps:spPr>',
    '<a:xfrm>',
    '<a:off x="0" y="0"/>',
    `<a:ext cx="${widthEmu}" cy="${heightEmu}"/>`,
    '</a:xfrm>',
    '<a:prstGeom prst="rect">',
    '<a:avLst/>',
    '</a:prstGeom>',
    '<a:noFill/>',
    '</wps:spPr>',
    '<wps:bodyPr rot="0" vert="horz" anchor="t" anchorCtr="0" rtlCol="0"/>',
    '<wps:txBody>',
    '<a:bodyPr/>',
    '<a:lstStyle/>',
    paragraphs,
    '</wps:txBody>',
    '</wps:wsp>',
    '</a:graphicData>',
    '</a:graphic>',
    '</wp:anchor>',
    '</w:drawing>',
    '</w:r>',
    '</w:p>',
  ].join('');

  return wrapInFlatOpc(body);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Finds the section whose body contains the current selection.
 * Falls back to the last section if no intersection is detected (e.g. cursor
 * sits inside a header / footer / text-box shape).
 */
async function findSectionForSelection(
  context: Word.RequestContext
): Promise<Word.Section> {
  const selection = context.document.getSelection();
  const sections  = context.document.sections;
  sections.load('items');
  await context.sync();

  // Try to locate the section that contains the selection by comparing ranges.
  for (const section of sections.items) {
    const bodyRange    = section.body.getRange(Word.RangeLocation.whole);
    const intersection = bodyRange.intersectWithOrNullObject(selection);
    intersection.load('isEmpty');
    await context.sync();

    // intersectWithOrNullObject returns a null-object when ranges don't
    // overlap; we also guard against an empty intersection.
    if (!intersection.isNullObject && !intersection.isEmpty) {
      return section;
    }
  }

  // Fallback: use the last section in the document.
  return sections.items[sections.items.length - 1];
}

async function pinTableToBottom(
  context: Word.RequestContext,
  table: Word.Table
): Promise<void> {
  try {
    const tableRange = table.getRange(Word.RangeLocation.whole);
    const ooxmlProxy = tableRange.getOoxml();
    await context.sync();

    let ooxml = ooxmlProxy.value;
    
    // 1. Absolute positioning tag (often stripped by Word Web, but we keep it)
    const positionTag = `<w:tblpPr w:vertAnchor="margin" w:horzAnchor="margin" w:tblpYSpec="bottom" w:tblpXSpec="left" w:bottomFromText="0" w:topFromText="0" w:leftFromText="0" w:rightFromText="0"/>`;

    if (ooxml.includes('<w:tblPr>')) {
      ooxml = ooxml.replace('<w:tblPr>', `<w:tblPr>${positionTag}`);
    } else if (ooxml.includes('<w:tblPr ')) {
      ooxml = ooxml.replace(/(<w:tblPr[^>]*>)/, `$1${positionTag}`);
    }

    tableRange.insertOoxml(ooxml, Word.InsertLocation.replace);
    await context.sync();
  } catch (err) {
    // Failsafe: if OOXML fails, the table just remains inline at cursor
    logger.warn('Failed to pin footnote table to bottom; leaving it inline', { error: err });
  }
}

/**
 * Inserts a footnote table at the cursor but uses OOXML to "float"
 * it to the bottom of the current page.
 */
async function insertFootnoteElement(profile: TemplateProfile): Promise<void> {
  try {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      
      const footnoteTable = selection.insertTable(
        1, 1,
        Word.InsertLocation.after,
        [[`1. ${FOOTNOTE_PLACEHOLDER}`]]
      );

      TableBuilder.clearAllBorders(footnoteTable);
      footnoteTable.autoFitBehavior('FixedSize');
      footnoteTable.width = DEFAULT_FOOTNOTE_WIDTH_PT;
      footnoteTable.alignment = Word.Alignment.left;

      const cell = footnoteTable.getCell(0, 0);
      TableBuilder.clearCellBorders(cell);
      TableBuilder.zeroCellPadding(cell);
      cell.verticalAlignment = Word.VerticalAlignment.bottom;

      cell.body.paragraphs.load('items');
      await context.sync();

      const paragraph = cell.body.paragraphs.items[0];
      paragraph.alignment   = Word.Alignment.left;
      paragraph.spaceBefore = 0;
      paragraph.spaceAfter  = 0;
      paragraph.leftIndent  = 0;
      paragraph.rightIndent = 0;
      paragraph.lineSpacing = 12;
      paragraph.font.name   = profile.typography.fontFamily;
      paragraph.font.size   = Math.max(6, (profile.typography.sourceFontSize || 7) - 1);
      paragraph.font.bold   = false;
      paragraph.font.color  = toHex(profile.colorPalette.sourceText);

      await context.sync();

      // Final step: convert this inline table to a floating table
      // anchored to the bottom margin of the current page.
      await pinTableToBottom(context, footnoteTable);
    });
  } catch (err) {
    throw WordApiError.fromOfficeError(err, { operation: 'insertFootnoteElement' });
  }
}

// ─── Element definition (unchanged) ──────────────────────────────────────────

/** Element definition for a shared foot note block. */
export const footnoteElement: ElementDefinition = {
  id: 'footnote',
  label: 'Foot Note',
  icon: 'TextBulletListSquareRegular',
  description: 'Shared fixed-position foot note block',
  category: 'document',
  insertAsync: insertFootnoteElement,
};